import { images, type ImageName } from '@/generated/images'
import { imageSrcSet, imageUrl } from '@/lib/images'

export { imageUrl }

interface Props {
  name: ImageName
  alt: string
  /** Rendered width hint for the browser, e.g. "(min-width: 1024px) 66vw, 100vw". */
  sizes: string
  className?: string
  /** Above-the-fold image (the LCP): load eagerly at high priority. */
  priority?: boolean
}

/**
 * Responsive image from scripts/optimize-images.py: AVIF with a WebP fallback,
 * intrinsic width/height (no layout shift) and lazy loading unless `priority`.
 * Style it through `className`, which lands on the <img>.
 */
export function Picture({ name, alt, sizes, className, priority = false }: Props) {
  const { width, height } = images[name]
  // React 18 doesn't know `fetchPriority`; the lowercase DOM attribute passes through.
  const priorityAttrs = priority ? ({ fetchpriority: 'high' } as Record<string, string>) : {}
  return (
    <picture className="contents">
      <source type="image/avif" srcSet={imageSrcSet(name, 'avif')} sizes={sizes} />
      <source type="image/webp" srcSet={imageSrcSet(name, 'webp')} sizes={sizes} />
      <img
        src={imageUrl(name)}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        decoding={priority ? 'sync' : 'async'}
        className={className}
        {...priorityAttrs}
      />
    </picture>
  )
}
